import { capitalize } from 'lodash';
import { ChevronsDownUp, ChevronsUpDown } from 'lucide-react';
import { useState } from 'react';
import { useRecoilValue } from 'recoil';

import { Heading } from '@/components/heading';
import { Icon } from '@/components/icon';
import { CarrierLogo } from '@/components/modules/carrier/logo';
import { EmissionsBadge } from '@/components/modules/emissions/badge';
import { SegmentPredictedMessage } from '@/components/route-segment-panel/segment-predicted-message';
import { Text } from '@/components/text';
import { DayOfWeekCalendar } from '@/components/ui/day-of-week-calendar';
import { Pill } from '@/components/ui/pill';
import { VehicleTypeBadge } from '@/components/ui/vehicle-type-badge';
import { MODE_AIR, MODE_WATER, VEHICLE_TYPES } from '@/lib/constants';
import { ItineraryLegModel } from '@/lib/models/itinerary/types';
import { getVehicleDescription } from '@/lib/models/vehicle/utils/get-vehicle-description';
import { getCarrierByIdState } from '@/state/carriers';
import { getVehicleByIdState } from '@/state/vehicles';
import { getCarrierLongName, getCarrierName } from '@/utils/carrier';
import { convertModeType, formatDuration, formatTerminalName } from '@/utils/format';
import { formatDateRange } from '@/utils/format/date-time';
import { getPreferredIdentifier } from '@/utils/helpers';
import { tv } from '@/utils/styles';
import { getVehicleName, isFreighterAircraft } from '@/utils/vehicle';

import { JourneyCodeshare } from '../codeshare';

export interface JourneyLegProps {
  leg: ItineraryLegModel;
  className: string;
}

const JourneyLeg = ({ leg, className }: JourneyLegProps) => {
  const [metaDataVisible, setMetaDataVisible] = useState(false);

  const {
    operatingCarrierId,
    mode,
    sta,
    std,
    etd,
    eta,
    durationSeconds,
    emissions,
    vehicleId,
    flightNumber,
    isPredicted,
    operatingCalendar,
    fromTerminal,
    toTerminal,
    codeshares,
  } = leg;

  const isAir = mode === MODE_AIR;
  const isOcean = mode === MODE_WATER;

  // Get Carrier
  const operatingCarrier = useRecoilValue(getCarrierByIdState(operatingCarrierId));
  const operatingCarrierCode = getPreferredIdentifier(operatingCarrier?.identifiers || []);
  const operatingCarrierName = getCarrierName(operatingCarrier);
  const operatingCarrierLongName = getCarrierLongName(operatingCarrier);
  const operatingCarrierLine = codeshares ? codeshares.find((codeshare) => codeshare.isOperator) : undefined;
  const hasCodeshares = Boolean(codeshares?.length);

  // Segment Vehicle
  const vehicle = useRecoilValue(getVehicleByIdState(vehicleId));
  const vehicleCode = getPreferredIdentifier(vehicle?.identifiers || []);
  const vehicleName = getVehicleName(vehicle);
  const vehicleType = isAir ? capitalize(VEHICLE_TYPES.AIRCRAFT) : capitalize(VEHICLE_TYPES.VESSEL);
  const isFreighter = isFreighterAircraft(vehicle);
  const vehicleDescription = getVehicleDescription(vehicle);

  // Status
  const isDelayed = false; // status === DELAY_STATUS.DELAYED;

  const hasMetaData = fromTerminal || toTerminal;

  const { base, header, text, subText, grid, codeshareList, separator, footer, dow, toggleButton, toggleIcon } =
    styles();

  return (
    <div className={base({ className })}>
      {isDelayed && (
        <Pill variant="error" label="Possible delay" iconLeft="warning-outline" className="absolute right-6 top-6" />
      )}
      {isPredicted && <SegmentPredictedMessage />}
      {!hasCodeshares && operatingCarrierName && (
        <div className={header()}>
          {operatingCarrierCode ? <CarrierLogo carrier={operatingCarrier} variant="md" mode={mode} /> : null}
          <Heading variant="h4" className={subText({ className: 'text-base' })}>
            {operatingCarrierName}
          </Heading>
        </div>
      )}
      {hasCodeshares && (
        <div className="max-w-[70%]">
          <Heading variant="h5" className={text()}>
            Operators / VSA
          </Heading>
          <ul className={codeshareList()}>
            {codeshares?.map((codeshare) => {
              return <JourneyCodeshare key={codeshare.carrierId} codeshare={codeshare} mode={mode} />;
            })}
          </ul>
        </div>
      )}
      {std && sta && (
        <div className={grid()}>
          <div>
            <Heading variant="h5" className={text()}>
              {isOcean ? 'Estimated' : 'Scheduled'} Departure
            </Heading>
            <Text variant="secondary" className={subText({ isDelayed })}>
              {std}
            </Text>
            {etd && isDelayed && (
              <Text variant="secondary" className={subText({ className: 'text-error' })}>
                ETA. {etd}
              </Text>
            )}
          </div>
          <div>
            <Heading variant="h5" className={text()}>
              {isOcean ? 'Estimated' : 'Scheduled'} Arrival
            </Heading>
            <Text variant="secondary" className={subText({ isDelayed })}>
              {sta}
            </Text>
            {eta && isDelayed && (
              <Text variant="secondary" className={subText({ className: 'text-error' })}>
                ETA. {eta}
              </Text>
            )}
          </div>
        </div>
      )}
      <div className={grid()}>
        <div>
          <Heading variant="h5" className={text()}>
            Transit Duration
          </Heading>
          <Text variant="secondary" className={subText()}>
            {durationSeconds ? formatDuration(durationSeconds) : ''}
          </Text>
        </div>
        <div>
          <Heading variant="h5" className={text()}>
            Emissions
          </Heading>
          {emissions && <EmissionsBadge emissions={emissions} />}
        </div>
      </div>
      <span className={separator()}>
        {hasMetaData && (
          <button type="button" className={toggleButton()} onClick={() => setMetaDataVisible(!metaDataVisible)}>
            More details
            {metaDataVisible ? (
              <ChevronsDownUp className={toggleIcon()} />
            ) : (
              <ChevronsUpDown className={toggleIcon()} />
            )}
          </button>
        )}
      </span>
      {metaDataVisible && (
        <div className={grid()}>
          <div>
            <Heading variant="h5" className={text()}>
              Departure Terminal
            </Heading>
            <Text variant="secondary" className={subText({ className: 'text-sm' })}>
              {fromTerminal ? formatTerminalName(fromTerminal) : 'N/A'}
            </Text>
          </div>
          <div>
            <Heading variant="h5" className={text()}>
              Arrival Terminal
            </Heading>
            <Text variant="secondary" className={subText({ className: 'text-sm' })}>
              {toTerminal ? formatTerminalName(toTerminal) : 'N/A'}
            </Text>
          </div>
        </div>
      )}
      <div className={grid()}>
        <div>
          <span className="flex gap-1">
            <Icon name={convertModeType(mode)} color="text.primary" />
            <Text variant="sm" className={text()}>
              {vehicleType}
            </Text>
          </span>

          <div className="flex flex-wrap items-center gap-2">
            <Text variant="secondary" className={subText()}>
              {vehicleName || 'N/A'}
            </Text>
            <VehicleTypeBadge label={vehicleDescription} isActive={isFreighter} />
          </div>
        </div>
        {isOcean && (
          <div>
            <Text variant="sm" className={text()}>
              Vessel IMO
            </Text>
            <Text variant="secondary" className={subText()}>
              {vehicleCode?.value || 'N/A'}
            </Text>
          </div>
        )}
        {isAir && flightNumber && (
          <div>
            <Text variant="sm" className={text()}>
              Flight number
            </Text>
            <Text variant="secondary" className={subText({ className: 'font-medium' })}>
              {flightNumber}
            </Text>
          </div>
        )}
      </div>
      {operatingCalendar && (
        <div className={footer()}>
          <div className={dow()}>
            <DayOfWeekCalendar operatingDays={operatingCalendar.operatingDays} label="Operates" />
          </div>
          <Text className="m-0 text-grey-500">Effective: {formatDateRange(operatingCalendar.effectiveDates)}</Text>
        </div>
      )}
      {operatingCarrierLine && (
        <div className={footer()}>
          <Text className="m-0 italic text-grey-500">
            * This vessel is operated by {operatingCarrierName}
            {operatingCarrierLongName && operatingCarrierLongName !== operatingCarrierName
              ? ` (${operatingCarrierLongName})`
              : ''}{' '}
            on the {operatingCarrierLine.serviceName} service
          </Text>
        </div>
      )}
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'flex flex-col gap-5 rounded-md border border-grey-200 bg-white p-6 shadow-soft',
    grid: 'grid grid-cols-2 gap-4',
    text: 'mb-1 text-sm font-normal leading-relaxed text-grey-600',
    subText: 'm-0',
    header: 'flex items-center gap-2',
    separator: 'relative h-0 w-full border-t border-dashed border-grey-300',
    toggleButton:
      'absolute -top-[7px] right-0 flex items-center bg-white pl-2 text-xs text-grey-500 hover:text-text-primary',
    toggleIcon: 'ml-1 h-3 w-3',
    feature: 'rounded-sm bg-grey-200 px-1 text-xs text-grey-600',
    dow: 'flex items-center',
    codeshareList: 'flex flex-wrap gap-2',
    footer:
      '-mx-6 -mb-6 flex justify-between rounded-bl-md rounded-br-md border-t border-dashed border-grey-200 bg-grey-50 px-6 py-3 text-xs',
  },
  variants: {
    isFreighter: {
      true: {
        feature: 'bg-lightBlue-50 text-lightBlue-800',
      },
    },
    isDelayed: {
      true: {
        subText: 'line-through',
      },
    },
  },
});

export { JourneyLeg };
